import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Unsubscribe } from "firebase/messaging";
import { showError, showInfo } from "../../../utils/alert";
import { isFirebaseConfigured } from "../../../config/firebase";
import {
  fetchFcmToken,
  isPushSupported,
  listenToForegroundMessages,
  requestNotificationPermission,
} from "../services/pushNotificationService";
import { registerDeviceToken } from "../services/notificationApi";
import { ForegroundNotification, PushStatus } from "../type/notification.types";

interface UsePushNotificationsOptions {
  /**
   * When true, silently re-establishes the token + foreground listener on mount
   * if the user has already granted permission (no prompt shown). Defaults to
   * true so returning users keep receiving notifications.
   */
  autoInitialize?: boolean;
  /** Optional custom handler for foreground messages (overrides the toast). */
  onMessage?: (notification: ForegroundNotification) => void;
}

interface UsePushNotificationsResult {
  status: PushStatus;
  token: string | null;
  permission: NotificationPermission | null;
  isSupported: boolean;
  error: string | null;
  /** Prompts for permission (if needed) and registers the FCM token. */
  enableNotifications: () => Promise<string | null>;
}

/**
 * React hook that wires up Firebase Cloud Messaging for the current browser:
 * requests permission, obtains and registers an FCM token, and listens for
 * foreground messages. Background messages are handled by the service worker.
 *
 * The hook never throws — failures surface via `status`/`error` so callers can
 * render appropriate UI, and the rest of the app keeps working if Firebase is
 * unconfigured or unsupported.
 */
export const usePushNotifications = (
  options: UsePushNotificationsOptions = {},
): UsePushNotificationsResult => {
  const { autoInitialize = true, onMessage } = options;
  const { t } = useTranslation();

  const supported = isPushSupported() && isFirebaseConfigured();

  const [status, setStatus] = useState<PushStatus>(supported ? "idle" : "unsupported");
  const [token, setToken] = useState<string | null>(null);
  const [permission, setPermission] = useState<NotificationPermission | null>(
    supported ? Notification.permission : null,
  );
  const [error, setError] = useState<string | null>(null);

  const isMounted = useRef(true);
  const unsubscribeRef = useRef<Unsubscribe | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  // Default foreground handler: surface the notification as a toast.
  const handleForegroundMessage = useCallback((notification: ForegroundNotification) => {
    if (onMessageRef.current) {
      onMessageRef.current(notification);
      return;
    }
    const text = [notification.title, notification.body].filter(Boolean).join(" — ");
    if (text) showInfo(text);
  }, []);

  const startForegroundListener = useCallback(async () => {
    if (unsubscribeRef.current) return;
    unsubscribeRef.current = await listenToForegroundMessages(handleForegroundMessage);
  }, [handleForegroundMessage]);

  /** Full opt-in flow, safe to call from a click handler. */
  const enableNotifications = useCallback(async (): Promise<string | null> => {
    
    if (!supported) {
      setStatus("unsupported");
      return null;
    }

    setStatus("requesting");
    setError(null);

    try {
      const result = await requestNotificationPermission();
      
      if (!isMounted.current) {
        return null;
      }
      
      setPermission(result);

      if (result !== "granted") {
        setStatus("denied");
        return null;
      }
      const fcmToken = await fetchFcmToken();
      
      if (!isMounted.current) {
        return null;
      }

      if (!fcmToken) {
        setStatus("error");
        setError(t("push_notifications.token_failed"));
        return null;
      }

      // Best-effort backend registration — a failure here shouldn't block
      // foreground notifications from working.
      try {
        await registerDeviceToken(fcmToken);
      } catch (registrationError) {
        console.error("[push] Failed to register token with backend:", registrationError);
      }

      await startForegroundListener();

      if (!isMounted.current) {
        return null;
      }
      
      setToken(fcmToken);
      setStatus("enabled");
      return fcmToken;
    } catch (err) {
      console.error("[push] Failed to enable notifications:", err);
      if (isMounted.current) {
        const message = t("push_notifications.enable_failed");
        setStatus("error");
        setError(message);
        showError(message);
      }
      return null;
    }
  }, [supported, startForegroundListener, t]);

  // Auto-initialize for users who already granted permission, and always clean
  // up the foreground listener on unmount.
  useEffect(() => {
    isMounted.current = true;

    if (autoInitialize && supported && Notification.permission === "granted") {
      void enableNotifications();
    }

    return () => {
      isMounted.current = false;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
  }, [autoInitialize, supported, enableNotifications]);

  return { status, token, permission, isSupported: supported, error, enableNotifications };
};
